import { useEffect, useMemo, useState } from "react";
import { validatePhoneNumberLength } from "libphonenumber-js";
import { z } from "zod";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiContentDrawer,
  MiDetailFields,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  INPUT_TYPES,
  PREFERRED_CONTACT_METHOD_OPTIONS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import {
  ClientFilter,
  capitalize,
  debounce,
  getPhoneNumber,
} from "@miplatform/utils";
import { mdiContacts } from "@mdi/js";
import { ContactsListSection, MiContactCard } from "@mibuildweb/components";
import { Box, useTheme } from "@mui/material";
import { useAccountContactsTabViewModel } from "./useAccountsContactsTabViewModel";

const DEFAULT_CONTACT = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  title: "",
  preferredContactMethod: "Phone",
  isVIP: false,
};

export function AccountContactsTab({ stateManager, accountDetail }) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const theme = useTheme();
  const edit = useEdit({ ...DEFAULT_CONTACT });

  const { contacts, getContacts, isLoading, createContact } =
    useAccountContactsTabViewModel({
      accountId: accountDetail?.id,
      stateManager,
    });
  const [filteredData, setFilteredData] = useState(contacts);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (accountDetail?.id) {
      getContacts();
    }
  }, [accountDetail?.id]);

  const clientFilterManager = useMemo(() => {
    const filter = new ClientFilter(
      contacts,
      ["firstName lastName", "title"],
      setFilteredData
    );

    filter.handleSearch(search);

    return filter;
  }, [contacts]);

  const handleSearch = (value) => {
    setSearch(value);
    debounce(clientFilterManager.handleSearch(value));
  };

  const contactFields = [
    {
      label: "Account",
      value: accountDetail?.name,
      required: true,
      width: "50%",
    },
    {
      label: "Title",
      name: "title",
      width: "50%",
    },
    {
      label: "First Name",
      name: "firstName",
      required: true,
      width: "50%",
      beforeOnChange: capitalize,
      validationRule: z.string().min(1, "First name is required"),
    },
    {
      label: "Last Name",
      name: "lastName",
      required: true,
      width: "50%",
      beforeOnChange: capitalize,
      validationRule: z.string().min(1, "Last name is required"),
    },
    {
      label: "Phone Number",
      name: "phone",
      value: getPhoneNumber(edit.getValue("phone")),
      required: true,
      width: "50%",
      handleChange: (e) => {
        if (
          validatePhoneNumberLength(getPhoneNumber(e.target.value), "US") ===
          "TOO_LONG"
        ) {
          return;
        }

        edit.update({
          phone: getPhoneNumber(e.target.value),
        });
      },
      validationRule: z
        .string()
        .min(1, "Phone number is required")
        .refine(
          (value) => validatePhoneNumberLength(value, "US") === undefined,
          {
            message: "Please enter a valid phone number",
          }
        ),
    },
    {
      label: "Email",
      name: "email",
      required: true,
      width: "50%",
      validationRule: z
        .string()
        .min(1, "Email is required")
        .email("Please enter a valid email address"),
    },
    {
      label: "Preferred Method",
      name: "preferredContactMethod",
      width: "11%",
      fieldType: INPUT_TYPES.SELECT,
      options: PREFERRED_CONTACT_METHOD_OPTIONS,
      validationRule: z.string().min(1, "Preferred contact method is required"),
    },
    {
      label: "VIP",
      name: "isVIP",
      fieldType: INPUT_TYPES.CHECKBOX,
      width: "auto",
      validationRule: z.boolean().optional(),
    },
  ];
  edit.setValidation(contactFields);

  const handleDrawerClose = () => {
    edit.reset();
    setIsDrawerOpen(false);
  };

  const handleCreateContact = async () => {
    const response = await createContact({
      accountId: accountDetail?.id,
      ...edit.getValue(),
    });
    handleDrawerClose();
    if (response) {
      getContacts();
    }
  };

  const renderContactCard = (contact) => (
    <Box key={`${contact.id}${contact.level}${contact.label}`}>
      <MiContactCard
        isPrimaryDisabled
        contact={contact}
        accountId={contact.accountId}
      />
    </Box>
  );
  return (
    <MiBox height="100%">
      <MiBuildPageHeader
        title="All Contacts"
        hasDivider={false}
        titleFontSize={18}
        search={search}
        searchPlaceholder="Find Contact"
        onHandleSearch={(e) => handleSearch(e.target.value)}
        actionComponent={
          <MiButton
            title="Add Contact"
            variant={BUTTON_VARIANTS.OUTLINED}
            onClick={() => setIsDrawerOpen(true)}
            data-testid="add-contact-btn"
          />
        }
      />
      <Box>
        <ContactsListSection
          key={accountDetail?.name}
          items={filteredData}
          showPrimaryToggle={false}
          renderCard={renderContactCard}
        />
      </Box>
      <MiContentDrawer
        variant="temporary"
        title="Add Contact"
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        leftIcon={mdiContacts}
        isLoading={isLoading}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            size: "medium",
            onClick: handleDrawerClose,
            color: BUTTON_COLORS.BLACK,
          },
          {
            name: "Submit",
            variant: BUTTON_VARIANTS.CONTAINED,
            color: BUTTON_COLORS.PRIMARY,
            size: "medium",
            disabled: !edit.isValid() || isLoading,
            onClick: handleCreateContact,
          },
        ]}
      >
        <MiDetailFields
          customWrapperStyle={{ padding: theme.spacing(3) }}
          detailFields={contactFields}
          edit={edit}
        />
      </MiContentDrawer>
    </MiBox>
  );
}
